const VOUCHER_CATEGORY_KEYWORDS = ['VOUCHER'];

export const isVoucherCategory = (categoryName = '') => {
  const upper = String(categoryName).toUpperCase();
  return VOUCHER_CATEGORY_KEYWORDS.some((keyword) => upper.includes(keyword));
};

export const voucherSellingPrice = (v) => {
  if (v?.selling_price != null && String(v.selling_price).trim() !== '') {
    return Number(v.selling_price);
  }
  const n = v?.price_non_member ?? v?.price_member;
  return Number(n || 0);
};

export const buildVoucherOptionsFromList = (vouchers, items, getCartQtyForVoucher) => {
  const active = (Array.isArray(vouchers) ? vouchers : []).filter((v) => v && v.status !== false);
  const byCat = {};
  active.forEach((v) => {
    const catKey = (v.voucher_category || 'VOUCHER').toString();
    if (!byCat[catKey]) byCat[catKey] = [];
    byCat[catKey].push(v);
  });
  const keys = Object.keys(byCat).sort((a, b) => a.localeCompare(b));
  const options = [];
  keys.forEach((catKey) => {
    options.push({ value: `header-voucher-${catKey}`, label: catKey, isHeader: true });
    byCat[catKey].forEach((v) => {
      const id = v.id;
      const price = voucherSellingPrice(v);
      const currentCartQty = getCartQtyForVoucher(id);
      const availableQuantity = null;
      const isDisabled = availableQuantity !== null && currentCartQty >= availableQuantity;
      options.push({
        value: `voucher-${id}`,
        label: v.name || 'Voucher',
        right: `$${Number(price).toFixed(2)}`,
        price,
        gst: v.gst,
        name: v.name || 'Voucher',
        category: catKey,
        isVoucher: true,
        availableQuantity,
        fullProductData: v,
        disabled: isDisabled,
      });
    });
  });
  return options;
};

export const createEmptyVoucherCodes = (qty = 1) =>
  Array.from({ length: Math.max(1, qty) }, () => '');

export const normalizeVoucherCodes = (codes = [], qty = 1) => {
  const q = Math.max(1, qty);
  const next = Array.isArray(codes) ? [...codes] : [];
  if (next.length > q) {
    return next.slice(next.length - q);
  }
  while (next.length < q) {
    next.unshift('');
  }
  return next;
};

export const voucherVerifySlotKey = (lineId, codeIdx) => `${lineId}-${codeIdx}`;

export const remapVoucherVerifyForQtyChange = (prev, lineId, beforeCodes, afterCodes) => {
  if (!lineId) return prev;
  const prefix = `${lineId}-`;
  const before = Array.isArray(beforeCodes) ? beforeCodes : [];
  const after = Array.isArray(afterCodes) ? afterCodes : [];
  const byOldIndex = {};
  Object.keys(prev).forEach((k) => {
    if (!k.startsWith(prefix)) return;
    const idx = Number(k.slice(prefix.length));
    if (!Number.isNaN(idx) && prev[k]) byOldIndex[idx] = prev[k];
  });
  const next = { ...prev };
  Object.keys(next).forEach((k) => {
    if (k.startsWith(prefix)) delete next[k];
  });
  after.forEach((serial, newIdx) => {
    const trimmed = String(serial ?? '').trim();
    if (!trimmed) return;
    const oldIdx = before.findIndex((c) => String(c ?? '').trim() === trimmed);
    if (oldIdx >= 0 && byOldIndex[oldIdx]) {
      next[voucherVerifySlotKey(lineId, newIdx)] = byOldIndex[oldIdx];
    }
  });
  return next;
};

export const isVoucherVerifyResponseOk = (resp) =>
  Boolean(
    resp?.success === true ||
      resp?.data?.success === true ||
      (resp?.data && typeof resp.data === 'object' && resp.data.success === true)
  );

export const getVoucherVerifySuccessMessage = (resp) =>
  resp?.data?.message || resp?.message || 'Voucher verified successfully.';

export const getVoucherVerifyErrorMessage = (resp) =>
  resp?.data?.message ||
  resp?.message ||
  (Array.isArray(resp?.errors) ? resp.errors.join(', ') : null) ||
  'Voucher verification failed';

const collectServiceIdsFromRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.service_id ?? row?.serviceId ?? row?.id))
    .filter((id) => Number.isFinite(id) && id > 0);

const collectServiceIdsFromCustomerItems = (customerItems) => {
  if (!customerItems || typeof customerItems !== 'object') return [];
  const ids = [];
  Object.values(customerItems).forEach((rows) => {
    if (!Array.isArray(rows)) return;
    rows.forEach((row) => {
      const id = Number(row?.service_id ?? row?.serviceId);
      if (Number.isFinite(id) && id > 0) ids.push(id);
    });
  });
  return ids;
};

export const extractServiceIdsFromBooking = (booking, customerItems = null) => {
  let ids = [];

  if (booking && typeof booking === 'object') {
    const rawServices =
      booking.booking_items ||
      booking.services ||
      booking.booking_services ||
      null;

    if (Array.isArray(rawServices)) {
      ids = collectServiceIdsFromRows(rawServices);
    }

    const bookingItem = booking.booking_item;
    if (bookingItem) {
      if (Array.isArray(bookingItem)) {
        ids = ids.concat(collectServiceIdsFromRows(bookingItem));
      } else if (typeof bookingItem === 'object') {
        if ('service_id' in bookingItem || 'serviceId' in bookingItem) {
          ids = ids.concat(collectServiceIdsFromRows([bookingItem]));
        } else {
          Object.values(bookingItem).forEach((value) => {
            if (Array.isArray(value)) {
              ids = ids.concat(collectServiceIdsFromRows(value));
            } else if (value && typeof value === 'object') {
              ids = ids.concat(collectServiceIdsFromRows([value]));
            }
          });
        }
      }
    }
  }

  if (ids.length === 0) {
    ids = ids.concat(collectServiceIdsFromCustomerItems(customerItems));
  }

  return [...new Set(ids)];
};

/** Normalize payment method slug/title for cash voucher detection. */
export const normalizePaymentMethodKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const isCashVoucherPaymentMethod = (slug, title) => {
  const slugKey = normalizePaymentMethodKey(slug);
  const titleKey = normalizePaymentMethodKey(title);
  return slugKey === 'cash_voucher' || titleKey === 'cash_voucher';
};

/**
 * Face / redeemable value from verify response voucher payload.
 * Prefers remaining/redeemable balance, then listed cash value fields.
 */
export const getCashVoucherFaceValue = (voucher) => {
  if (!voucher || typeof voucher !== 'object') return null;
  const candidates = [
    voucher.current_value,
    voucher.remaining_value,
    voucher.redeemable_value,
    voucher.voucher_value,
    voucher.face_value,
    voucher.balance,
    voucher.current_balance,
    voucher.original_value,
    voucher.value,
    voucher.amount,
    voucher.selling_price,
    voucher.price,
  ];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return Math.round(n * 100) / 100;
  }
  return null;
};

export const getCashVoucherId = (voucher) => {
  if (!voucher || typeof voucher !== 'object') return null;
  const id = voucher.id ?? voucher.voucher_id ?? voucher.voucherId;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const isCartVoucherSerialPresent = (cartItems, serial) => {
  const normalized = String(serial || '').trim();
  if (!normalized) return false;
  return cartItems.some((it) => {
    if (!it.isVoucher) return false;
    const codes = normalizeVoucherCodes(it.voucherCodes, it.qty);
    return codes.some((c) => String(c || '').trim() === normalized);
  });
};

export const getVoucherFromVerifyResponse = (resp) =>
  resp?.data?.data?.voucher || resp?.data?.voucher || null;

const isTruthyFlag = (raw) => raw === true || raw === 1 || raw === '1' || raw === 'true';

/** Verify-response data object that sits next to `voucher` (grace fields may live on either). */
const getVerifyResponseData = (resp) => resp?.data?.data || resp?.data || null;

export const isCashVoucherGracePeriod = (voucher, resp = null) => {
  const data = getVerifyResponseData(resp);
  return isTruthyFlag(
    voucher?.is_grace_period ?? voucher?.isGracePeriod ?? data?.is_grace_period ?? data?.isGracePeriod
  );
};

export const getCashVoucherExpiryDate = (voucher, resp = null) => {
  const data = getVerifyResponseData(resp);
  return voucher?.expiry_date ?? voucher?.expiryDate ?? data?.expiry_date ?? data?.expiryDate ?? null;
};

/** "2026-06-30" -> "30 June 2026". Date-only strings are parsed as local dates to avoid TZ shifts. */
export const formatVoucherExpiryDate = (raw) => {
  if (!raw) return '';
  const str = String(raw).trim();
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(str);
  if (Number.isNaN(date.getTime())) return str;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const findVoucherCatalogProduct = (catalog, voucherPayload) => {
  const list = Array.isArray(catalog) ? catalog : [];
  if (!voucherPayload) return null;

  const productId =
    voucherPayload.voucher_product_id ??
    voucherPayload.product_id ??
    voucherPayload.voucher_product?.id;
  if (productId != null) {
    const byId = list.find((p) => Number(p.id) === Number(productId));
    if (byId) return byId;
  }

  const targetName = String(voucherPayload.product_name || voucherPayload.name || '')
    .trim()
    .toLowerCase();
  if (!targetName) return null;

  return (
    list.find((p) => String(p.name || '').trim().toLowerCase() === targetName) ||
    list.find((p) => String(p.name || '').trim().toLowerCase().includes(targetName)) ||
    null
  );
};

export const buildOptionFromVoucherCatalogProduct = (product) => {
  const price = voucherSellingPrice(product);
  const catKey = (product.voucher_category || 'VOUCHER').toString();
  return {
    value: `voucher-${product.id}`,
    label: product.name || 'Voucher',
    right: `$${Number(price).toFixed(2)}`,
    price,
    gst: product.gst,
    name: product.name || 'Voucher',
    category: catKey,
    isVoucher: true,
    availableQuantity: null,
    fullProductData: product,
    disabled: false,
  };
};

export const isEditableInputFocused = () => {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return false;
  if (el.readOnly || el.disabled) return false;
  return true;
};
